import React, {
  Fragment,
  FC,
  useRef,
  useState,
  useEffect,
  CSSProperties,
} from 'react'
import classNamesBind from 'classnames/bind'

import ArchetypesChart from './components/ArchetypesChart'
import Breakdown from './components/Breakdown'
import DiscChart from './components/DiscChart'
import PointSummary from './components/PointSummary'
import ProfileBubble from '@dashboard/components/ProfileBubble'
import LoadingBox from '@dashboard/components/LoadingBox'
import ColorIcon from '@dashboard/components/ColorIcon'
import BarGraph from './components/BarGraph'
import Pagination from '@dashboard/components/Pagination'
import Loader from '@crystal-eyes/components/elements/Loader/Loader'

import { oxfordComma } from '@dashboard/lib/string'
import {
  circumplexPoint,
  toDiscType,
  GetOrderedList,
  toArchetype,
  typeTraits,
  CircumplexVectorWithId,
  distributeCircumplexes,
  getCircumplexScores,
  getAverageVectorDegreeAndIntensity,
} from '@dashboard/lib/personality'
import {
  Profile,
  ColorIconName,
  DiscType,
  CircumplexBreakdownValue,
  HiringBehavior,
  LoadingStates,
} from '@dashboard/lib/types'
import ReactiveOverlay from '@dashboard/components/ReactiveOverlay'
import { fullName } from '@dashboard/lib/profile'
import GroupProfileCluster from '@dashboard/components/MyTeam/components/GroupProfileCluster'
import { CircumplexRoleTypes, CircumplexPointBreakdown } from './types'

import css from './styles.module.scss'
const classNames = classNamesBind.bind(css)

const Maximize = '/static/share/images/maximize.svg'

const BUBBLE_SIZE = 11

interface CustomAction {
  icon: ColorIconName
  onClick: () => void
}

interface OwnProps {
  size: number
  breakdown?: Partial<Record<DiscType, CircumplexBreakdownValue>>
  breakdownArrowBehavior?: 'next_type' | 'next_profile'
  breakdownState?: LoadingStates
  children?: React.ReactNode
  hideButtons?: boolean
  isCTA?: boolean
  pointBreakdown?: CircumplexPointBreakdown
  points?: Array<HiringBehavior>
  profiles?: Array<Profile>
  profileSize?: number
  textType?: DiscType | null
  highlightType?: DiscType
  highlightTypeGrey?: DiscType
  selectedType?: DiscType
  showMaximize?: boolean
  showDiscChart?: boolean
  hideBarChart?: boolean
  maximizedSize?: number
  maximizeSmall?: boolean
  avoidCollisions?: boolean
  activeTypes?: (DiscType | 'empty')[]
  customActions?: CustomAction[]
  hideDiscLetters?: boolean
  imagePoints?: { src: string; degrees: number; intensity: number }[]
  isLoading?: boolean
  currentPage?: number
  totalPages?: number
  profileIcons?: ColorIconName[]
  showDiscLetters?: boolean
  breakdownInteraction?: () => void
  onTypeSelected?: (type: DiscType) => void
  onTypeHovered?: (type: DiscType) => void
  changePage?: (page: number) => void
}

const CircumplexGraph: FC<OwnProps> = ({
  size,
  breakdown,
  breakdownArrowBehavior,
  breakdownState,
  hideButtons,
  isCTA,
  pointBreakdown,
  points,
  profiles,
  profileSize,
  textType,
  highlightType,
  highlightTypeGrey,
  selectedType: selectedTypeParam,
  showMaximize,
  showDiscChart: showDiscChartParam,
  hideBarChart,
  maximizedSize,
  maximizeSmall,
  activeTypes,
  customActions,
  hideDiscLetters,
  imagePoints,
  profileIcons,
  showDiscLetters,
  isLoading,
  currentPage,
  totalPages,
  changePage,
  breakdownInteraction,
  onTypeSelected,
  onTypeHovered,
}: OwnProps) => {
  const containerRef = useRef<HTMLDivElement>(null)

  const [roleTypes, setRoleTypes] = useState<CircumplexRoleTypes>({})
  const [selectedType, setSelectedType] = useState<DiscType | null>(
    selectedTypeParam || null,
  )
  const [showDiscChart, setShowDiscChart] = useState(
    showDiscChartParam || false,
  )
  const [textTypeToggled, setTextTypeToggled] = useState(false)
  const [maximized, setMaximized] = useState(false)
  const [hoveredType, setHoveredType] = useState<DiscType | null>(null)
  const [barGraphProfile, setBarGraphProfile] = useState<Profile | null>(
    profiles && !hideBarChart ? profiles[0] : null,
  )
  const [barGraphDropdownOpen, setBarGraphDropdownOpen] = useState(false)

  const adjustedSize = maximized ? maximizedSize || size : size

  useEffect(() => {
    if (textType) setRoleTypes(getRoleArray(textType))
  }, [textType])

  useEffect(() => {
    document.addEventListener('click', outsideClick)

    return () => document.removeEventListener('click', outsideClick)
  }, [barGraphDropdownOpen])

  useEffect(() => {
    if (selectedTypeParam) setSelectedType(selectedTypeParam)
  }, [selectedTypeParam])

  // Functions
  const showDisc = (choice: boolean) => {
    setShowDiscChart(choice)
  }

  const selectType = (type: DiscType) => {
    if (onTypeSelected) {
      onTypeSelected(type)
    }

    if (onTypeSelected || breakdown) {
      setSelectedType(type)
    }
  }

  const getRoleArray = (type: DiscType) => {
    const order = GetOrderedList()

    const roleArray = {
      main: '',
      firstTier: [] as string[],
      secondTier: [] as string[],
      thirdTier: [] as string[],
    }

    if (order.includes(type)) {
      const index = order.indexOf(type)

      roleArray.main = order[index]
      roleArray.firstTier.push(getModIndex(index - 1))
      roleArray.firstTier.push(getModIndex(index + 1))
      roleArray.secondTier.push(getModIndex(index - 2))
      roleArray.secondTier.push(getModIndex(index + 2))
      roleArray.thirdTier.push(getModIndex(index - 3))
      roleArray.thirdTier.push(getModIndex(index + 3))
    }

    return roleArray
  }

  const getBehaviorFit = (point: HiringBehavior) => {
    if (!pointBreakdown) return ''

    // Get circumplex distance
    const profileDegrees = pointBreakdown.circumplex?.degrees || 0
    let distance = Math.abs(point.circumplex.degrees - profileDegrees) % 360
    distance = distance > 180 ? 360 - distance : distance
    distance = Math.round((1 - distance / 180) * 100)

    // Decide on text based on distance
    const name = pointBreakdown ? pointBreakdown.first_name : ''
    let secondText = `This may require significant adjustment from ${name}, which may <strong>require a lot of energy.</strong>`
    if (distance > 66) {
      secondText = `This is likely to come naturally to ${name} and <strong>require very little energy.</strong>`
    } else if (distance > 33) {
      secondText = `This may be a moderate adjustment for ${name}, and <strong>require some energy.</strong>`
    }

    return secondText
  }

  const getModIndex = (index: number) => {
    const order = GetOrderedList()

    const finalIndex = order.length - 1

    if (index > finalIndex) return order[index - finalIndex - 1]
    if (index < 0) return order[finalIndex + index + 1]
    return order[index]
  }

  const changeTextType = (showCandidate: boolean) => {
    if (!pointBreakdown) return null

    const discLetter = pointBreakdown.textType
    const expectedDiscLetter = pointBreakdown.expectedTextType

    if (showCandidate) {
      setTextTypeToggled(true)
      setRoleTypes(getRoleArray(expectedDiscLetter))
    } else {
      setTextTypeToggled(false)
      setRoleTypes(getRoleArray(discLetter))
    }
  }

  const toggleMaximized = () => {
    setMaximized(!maximized)
  }

  const onTypeHoveredHandler = (discType: DiscType | null) => {
    if (onTypeHovered && discType) onTypeHovered(discType)

    if (hoveredType == discType) return null

    setHoveredType(discType)
  }

  const showBarChart = () => {
    return showDiscChart && barGraphProfile
  }

  const clickDiscButton = () => {
    if (profiles && profiles.length > 1 && !hideBarChart) {
      setBarGraphDropdownOpen(!barGraphDropdownOpen)
    } else {
      showDisc(true)
    }
  }

  const selectBarGraphProfile = (profile: Profile) => {
    showDisc(true)
    setBarGraphProfile(profile)
  }

  const outsideClick = (e: MouseEvent) => {
    e.stopPropagation()
    const segmentRef = containerRef.current

    const noSegment = segmentRef && !segmentRef.contains(e.target as Node)

    if (noSegment && barGraphDropdownOpen) {
      setBarGraphDropdownOpen(false)
    }
  }

  const getScores = (): CircumplexVectorWithId[] => {
    return getCircumplexScores(profiles || [])
  }

  // Partials
  const renderHoveredType = () => {
    const type = hoveredType

    if (!type) return null

    return (
      <div
        className={classNames('hover-traits', type)}
        onClick={() => setSelectedType(null)}
      >
        <div className={classNames('hover-title', type)}>
          {toArchetype(type)} ({type})
        </div>
        <div className={classNames('hover-title')}>
          {oxfordComma(typeTraits(type))}
        </div>
      </div>
    )
  }

  const renderChart = () => {
    if (showBarChart() && barGraphProfile) {
      return (
        <BarGraph
          hasBreakdown={!!breakdown}
          profile={barGraphProfile}
          height={size}
        />
      )
    }

    if (showDiscChart) {
      return (
        <DiscChart
          hasBreakdown={!!breakdown}
          selectType={selectType}
          selectedType={selectedType}
          roleTypes={roleTypes}
          breakdownInteraction={breakdownInteraction}
          showCursorAsPointer={!!onTypeSelected}
          onTypeHovered={onTypeHoveredHandler}
          activeTypes={activeTypes}
          highlightType={highlightType}
          highlightTypeGrey={highlightTypeGrey}
          hideDiscLetters={hideDiscLetters}
        />
      )
    }

    return (
      <ArchetypesChart
        hasBreakdown={!!breakdown}
        selectType={selectType}
        selectedType={selectedType}
        roleTypes={roleTypes}
        breakdownInteraction={breakdownInteraction}
        showCursorAsPointer={!!onTypeSelected}
        onTypeHovered={onTypeHoveredHandler}
        activeTypes={activeTypes}
        maximizeSmall={maximizeSmall}
        showDiscLetters={showDiscLetters}
      />
    )
  }

  const renderProfileDots = () => {
    const adjustedScores = distributeCircumplexes(getScores(), BUBBLE_SIZE)

    if (showBarChart()) return null

    if (isCTA) return <span key="???">{renderDot(adjustedScores)}</span>

    if (profiles) {
      return adjustedScores.map(renderDotNew)
    }

    return null
  }

  const renderImagePoints = () => {
    if (!imagePoints) return null

    return imagePoints.map(imagePoint => {
      return <span key={imagePoint.src}>{renderImagePoint(imagePoint)}</span>
    })
  }

  const renderMaximize = () => {
    if (!showMaximize || !breakdown) return null

    return (
      <img
        onClick={toggleMaximized}
        src={Maximize}
        className={classNames('maximize')}
      />
    )
  }

  const renderCustomActions = () => {
    if (!customActions) return null

    return customActions.map(({ icon, onClick }) => {
      return (
        <div
          key={icon}
          onClick={onClick}
          className={classNames('customAction')}
        >
          <ColorIcon icon={icon} width={24} />
        </div>
      )
    })
  }

  const renderDotNew = (scores: CircumplexVectorWithId[], i: number) => {
    let chartSize = size ? size : 100
    if (maximized) chartSize = chartSize * 2
    let discPosition: CSSProperties = { bottom: '50%', left: '50%' }
    const isCluster = scores.length > 1
    const { degrees, intensity } = getAverageVectorDegreeAndIntensity(scores)

    if (!isCTA && scores) {
      const [left, bottom] = circumplexPoint(degrees, intensity)

      discPosition = {
        bottom: `${bottom}%`,
        left: `${left}%`,
        marginLeft: `-${BUBBLE_SIZE / 2}px`,
        marginBottom: `-${BUBBLE_SIZE / 2}px`,
      }
    }

    if (profiles) {
      const finalSize = profileSize ? profileSize : chartSize / BUBBLE_SIZE

      if (isCluster) {
        const profilesToRender = scores
          .map(score => profiles.find(p => p.id === score.id))
          .filter((p): p is Profile => !!p)
        return (
          <div
            className={classNames('groupProfileClusterContainer')}
            style={discPosition}
            key={`single-key-${i}`}
          >
            <GroupProfileCluster
              profiles={profilesToRender}
              size={finalSize}
              discType={toDiscType(degrees)}
              hidePercentage
            />
          </div>
        )
      }
      const profile = profiles.find(p => p.id === scores[0].id)
      if (!profile) return null

      const styles = {
        ...discPosition,
        width: `${finalSize}px`,
        maxHeight: `${finalSize}px`,
        marginLeft: `-${finalSize / 2}px`,
        marginBottom: `-${finalSize / 2}px`,
      }

      return (
        <span key={profile.id || `${profile.first_name}-${profile.last_name}`}>
          <div className={classNames('radar-chart-dot')} style={styles}>
            <ProfileBubble profile={profile} size={finalSize} shadow hover />
          </div>
        </span>
      )
    }
  }

  const renderDot = (
    adjustedScores: (CircumplexVectorWithId | null)[][],
    profile?: Profile,
    index?: number,
  ) => {
    let chartSize = size ? size : 100
    if (maximized) chartSize = chartSize * 2
    let discPosition = { bottom: '50%', left: '50%' }
    const dotSize = 11
    const adjustedScore = adjustedScores
      .flat()
      .find(score => score && score?.id === profile?.id)

    if (!isCTA && adjustedScore) {
      const [left, bottom] = circumplexPoint(
        adjustedScore.degrees,
        adjustedScore.intensity,
      )

      discPosition = {
        bottom: `${bottom}%`,
        left: `${left}%`,
      }
    }

    const finalSize = profileSize ? profileSize : chartSize / dotSize

    const styles = {
      ...discPosition,
      width: `${finalSize}px`,
      maxHeight: `${finalSize}px`,
      marginLeft: `-${finalSize / 2}px`,
      marginBottom: `-${finalSize / 2}px`,
    }

    if (!profile) return null

    const profileIcon = index === undefined ? null : (profileIcons || [])[index]

    if (profileIcon) {
      return (
        <div
          className={classNames(
            'radar-chart-dot',
            'isIcon',
            profile.personality?.disc_type,
          )}
          style={styles}
        >
          <ReactiveOverlay
            withTransform
            overlayGap={12}
            preferredDirection={'topRight'}
            overlayContent={
              <div className={classNames('tip')}>{fullName(profile)}</div>
            }
          >
            <ColorIcon
              icon={profileIcon}
              width={finalSize - 8}
              discType="white"
            />
          </ReactiveOverlay>
        </div>
      )
    }

    return (
      <div className={classNames('radar-chart-dot')} style={styles}>
        <ProfileBubble profile={profile} size={finalSize} shadow hover />
      </div>
    )
  }

  const renderImagePoint = ({
    src,
    degrees,
    intensity,
  }: {
    src: string
    degrees: number
    intensity: number
  }) => {
    let chartSize = size ? size : 100
    if (maximized) chartSize = chartSize * 2
    let discPosition = { bottom: '50%', left: '50%' }
    const dotSize = 11

    const [left, bottom] = circumplexPoint(degrees, intensity)

    discPosition = {
      bottom: `${bottom}%`,
      left: `${left}%`,
    }

    const styles = {
      ...discPosition,
      width: `${chartSize / dotSize}px`,
      maxHeight: `${chartSize / dotSize}px`,
      marginLeft: `-${chartSize / dotSize / 2}px`,
      marginBottom: `-${chartSize / dotSize / 2}px`,
    }

    return (
      <div className={classNames('radar-chart-dot')} style={styles}>
        <img className={classNames('imagePoint')} src={src} />
      </div>
    )
  }

  const renderButtons = () => {
    const hasManyProfiles = profiles && profiles?.length > 1
    const archetypesLabel = barGraphProfile ? 'Map' : 'Archetypes'

    if (hideButtons) return null

    return (
      <div className={classNames('buttons')}>
        <div
          className={classNames('button', { active: !showDiscChart })}
          onClick={() => showDisc(false)}
        >
          <div className={classNames('button-content')}>{archetypesLabel}</div>
        </div>
        <div
          className={classNames(
            'button',
            { wide: showDiscChart && barGraphProfile && hasManyProfiles },
            { active: showDiscChart },
          )}
          onClick={clickDiscButton}
          ref={containerRef}
        >
          <div className={classNames('button-content')}>
            {renderDiscLabel()}
          </div>
          {renderBarGraphProfiles()}
        </div>
        {renderMaximize()}
        {renderCustomActions()}
      </div>
    )
  }

  const renderPagination = () => {
    if (!totalPages || !changePage || !currentPage || totalPages < 2)
      return null

    return (
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        changePage={changePage}
      />
    )
  }

  const renderDiscLabel = () => {
    const hasManyProfiles = profiles && profiles?.length > 1
    let discLabel = barGraphProfile ? 'DISC' : 'DISC types'

    if (showDiscChart && barGraphProfile && hasManyProfiles) {
      discLabel = `DISC: ${barGraphProfile.first_name} ${barGraphProfile.last_name}`
    }

    if (barGraphProfile && hasManyProfiles) {
      return (
        <div
          className={classNames('label-wrapper', { center: !showDiscChart })}
        >
          <div className={classNames('label', { center: !showDiscChart })}>
            {discLabel}
          </div>

          <div
            className={classNames('arrow', { rotated: barGraphDropdownOpen })}
          >
            <ColorIcon width={14} icon="arrow" />
          </div>
        </div>
      )
    }

    return discLabel
  }

  const renderBarGraphProfiles = () => {
    if (!barGraphDropdownOpen || !profiles) return null

    return (
      <div className={classNames('bar-graph-profiles')}>
        {profiles.map(profile => {
          return (
            <div
              key={profile.id}
              className={classNames('bar-graph-profile')}
              onClick={() => selectBarGraphProfile(profile)}
            >
              {profile.first_name} {profile.last_name}
            </div>
          )
        })}
      </div>
    )
  }

  const renderBreakdown = () => {
    if (breakdownState == 'loading')
      return (
        <div className={classNames('loading-box')}>
          <LoadingBox text={'Loading insights...'} />
        </div>
      )

    if (breakdown)
      return (
        <Breakdown
          profiles={profiles || []}
          breakdown={breakdown}
          breakdownArrowBehavior={breakdownArrowBehavior}
          selectedType={selectedType}
          selectType={selectType}
          breakdownInteraction={breakdownInteraction}
        />
      )

    return null
  }

  const renderPoints = () => {
    if (!points || showBarChart()) return null

    return points.map(point => (
      <Fragment key={point.circumplex.degrees}>{renderPoint(point)}</Fragment>
    ))
  }

  const renderPoint = (point: HiringBehavior) => {
    let circumplex = [50, 50]
    const min = 30
    const adjustedIntensity =
      min + (100 - min) * (point.circumplex.intensity / 100)

    circumplex = circumplexPoint(point.circumplex.degrees, adjustedIntensity)

    const styles = {
      bottom: `${circumplex[1]}%`,
      left: `${circumplex[0]}%`,
    }

    const pointScore = point.score
    let level = 'a'
    if (pointScore > 3.2) {
      level = 'e'
    } else if (pointScore > 2.4) {
      level = 'd'
    } else if (pointScore > 1.6) {
      level = 'c'
    } else if (pointScore > 0.8) {
      level = 'b'
    }

    if (point) {
      return (
        <div
          className={classNames('radar-chart-dot', 'point', `${level}`)}
          style={styles}
        >
          {renderPointSummary(point)}
        </div>
      )
    }
  }

  const renderPointSummary = (point: HiringBehavior) => {
    const pointScore = point.score
    let text = 'very unimportant'
    if (pointScore > 3.2) {
      text = 'very important'
    } else if (pointScore > 2.4) {
      text = 'important'
    } else if (pointScore > 1.6) {
      text = 'neutral'
    } else if (pointScore > 0.8) {
      text = 'unimportant'
    }

    const discType = toDiscType(point.circumplex.degrees)

    if (pointBreakdown) {
      const secondText = getBehaviorFit(point)

      return (
        <div className={classNames('summary')}>
          <p>
            It is <strong>{text}</strong> for the person in this role to{' '}
            <i className={classNames(`${discType}`)}>
              {point.text.toLowerCase()}.
            </i>
          </p>
          <p dangerouslySetInnerHTML={{ __html: secondText }} />
        </div>
      )
    }

    return (
      <div className={classNames('summary')}>
        <p>
          It is <strong>{text}</strong> for the person in this role to{' '}
          <i className={classNames(`${discType}`)}>
            {point.text.toLowerCase()}.
          </i>
        </p>
      </div>
    )
  }

  const renderPointLargeSummary = () => {
    if (!pointBreakdown) return null

    return (
      <PointSummary
        pointBreakdown={pointBreakdown}
        toggle={changeTextType}
        textTypeToggled={textTypeToggled}
      />
    )
  }

  const renderLoading = () => {
    if (!isLoading) return null

    return (
      <div className={classNames('loader')}>
        <Loader size={40} blue />
      </div>
    )
  }

  return (
    <div className={classNames('flex', { maximized, maximizeSmall })}>
      <div
        className={classNames('container', { maximized, maximizeSmall })}
        style={{ width: `${adjustedSize}px` }}
      >
        {renderHoveredType()}

        <div className={classNames('wrapper')}>
          <div className={classNames('chart-wrapper')}>{renderChart()}</div>
          {renderProfileDots()}
          {renderImagePoints()}
          {renderPoints()}
          {renderLoading()}
        </div>
        {renderButtons()}
        {renderPagination()}
      </div>
      {renderBreakdown()}
      {renderPointLargeSummary()}
    </div>
  )
}

export default CircumplexGraph
